/**
 * Tab Config nell'Admin Panel.
 * Mostra ESSENTIAL (.env) e RUNTIME (DB) configurazioni.
 */

import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Eye, EyeOff, RotateCcw, AlertTriangle } from 'lucide-react'
import { configApi, ConfigItem, ConfigUpdate } from '../../api/config'
import { SkeletonList } from '../../components/Skeleton'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Alert,
  AlertDescription,
} from '@/components/ui/alert'

export function ConfigTab() {
  const [search, setSearch] = useState('')
  const [editingKey, setEditingKey] = useState<string | null>(null)
  const [editValue, setEditValue] = useState<string>('')
  const [showEssential, setShowEssential] = useState(false)

  const queryClient = useQueryClient()

  const { data: configs, isLoading, error } = useQuery({
    queryKey: ['config-list'],
    queryFn: configApi.list,
  })

  const updateMutation = useMutation({
    mutationFn: ({ key, value }: { key: string; value: string }) =>
      configApi.update(key, { value }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['config-list'] })
      setEditingKey(null)
      setEditValue('')
    },
    onError: (err: any) => {
      alert(err.response?.data?.detail || 'Errore durante l\'aggiornamento')
    },
  })

  const resetMutation = useMutation({
    mutationFn: configApi.reset,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['config-list'] })
    },
    onError: (err: any) => {
      alert(err.response?.data?.detail || 'Errore durante il reset')
    },
  })

  const handleReset = (key: string) => {
    if (confirm(`Resettare "${key}" al valore di default?`)) {
      resetMutation.mutate(key)
    }
  }

  // Separate configs by source
  const essentialConfigs = configs?.filter(c => c.source === 'essential') || []
  const runtimeConfigs = configs?.filter(c => c.source === 'runtime') || []

  // Filter by search
  const filterConfigs = (list: ConfigItem[]) => 
    list.filter(c => {
      if (!search) return true
      const s = search.toLowerCase()
      return c.key.toLowerCase().includes(s) || (c.description?.toLowerCase().includes(s))
    })

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Configurazione Sistema</h2>
          <p className="text-sm text-muted-foreground">
            ESSENTIAL = .env (non modificabile) | RUNTIME = DB (modificabile da UI)
          </p>
        </div>
      </div>

      {/* Search */}
      <div className="flex gap-4">
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Cerca per chiave o descrizione..."
          className="max-w-md"
        />
        <Button
          variant="outline"
          onClick={() => setShowEssential(!showEssential)}
        >
          {showEssential ? 'Nascondi Essential' : 'Mostra Essential (.env)'}
        </Button>
      </div>

      {isLoading ? (
        <SkeletonList count={10} />
      ) : error ? (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Errore caricamento configurazioni. Verificare connessione backend.
          </AlertDescription>
        </Alert>
      ) : (
        <>
          {/* Essential Configs (from .env) */}
          {showEssential && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Badge variant="destructive" className="bg-destructive/20 text-destructive border-destructive/30">
                    ESSENTIAL
                  </Badge>
                  <span>Configurazioni da .env</span>
                </CardTitle>
                <p className="text-sm text-muted-foreground">
                  Queste variabili sono in <code className="text-xs">.env</code> e non possono essere modificate da UI.
                  Contengono API keys, credenziali, secrets.
                </p>
              </CardHeader>
              <CardContent>
                <div className="border border-border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Chiave</TableHead>
                        <TableHead>Valore</TableHead>
                        <TableHead>Descrizione</TableHead>
                        <TableHead>Tipo</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filterConfigs(essentialConfigs).map((config) => (
                        <TableRow key={config.key}>
                          <TableCell className="font-mono font-medium text-sm">
                            {config.key}
                          </TableCell>
                          <TableCell className="font-mono text-sm text-muted-foreground">
                            {config.value}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground max-w-xs truncate">
                            {config.description}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="bg-destructive/10 text-destructive border-destructive/20">
                              ESSENTIAL
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Runtime Configs (from DB) */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Badge variant="default" className="bg-success/20 text-success border-success/30">
                  RUNTIME
                </Badge>
                <span>Configurazioni da Database</span>
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Queste variabili sono in <code className="text-xs">app_config</code> tabella.
                Modificabili da UI, salvate in DB. Priorità: DB &gt; .env &gt; default.
              </p>
            </CardHeader>
            <CardContent>
              <div className="border border-border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Chiave</TableHead>
                      <TableHead>Valore</TableHead>
                      <TableHead>Descrizione</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Azioni</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filterConfigs(runtimeConfigs).map((config) => (
                      <TableRow key={config.key}>
                        <TableCell className="font-mono font-medium text-sm">
                          {config.key}
                        </TableCell>
                        <TableCell>
                          {editingKey === config.key ? (
                            <div className="flex gap-2">
                              <Input
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                placeholder="Nuovo valore"
                                className="w-48"
                              />
                              <Button
                                size="sm"
                                onClick={() => {
                                  if (editValue) {
                                    updateMutation.mutate({ key: config.key, value: editValue })
                                  }
                                }}
                                disabled={updateMutation.isPending}
                              >
                                Salva
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => { setEditingKey(null); setEditValue('') }}
                              >
                                Annulla
                              </Button>
                            </div>
                          ) : (
                            <span className="font-mono text-sm">{config.value}</span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground max-w-xs truncate">
                          {config.description}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="bg-success/10 text-success border-success/20">
                            {config.type}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-auto p-0 text-primary hover:text-primary/80"
                              onClick={() => {
                                setEditingKey(config.key)
                                setEditValue(config.value)
                              }}
                              disabled={editingKey === config.key}
                            >
                              Modifica
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-auto p-0 text-muted-foreground hover:text-foreground"
                              onClick={() => handleReset(config.key)}
                              title="Reset a default"
                            >
                              <RotateCcw className="size-3" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  )
}